import { useRef, useState } from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import { CloudUpload, Psychology } from '@mui/icons-material';

export type VisionAIUploadDropzoneProps = {
  title: string;
  supportedFormatsText: string;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  onFilesSelected: (files: File[]) => void;
};

export default function VisionAIUploadDropzone({
  title,
  supportedFormatsText,
  accept = '.pdf,.jpg,.jpeg,.png,.gif,.bmp',
  multiple = false,
  disabled = false,
  onFilesSelected,
}: VisionAIUploadDropzoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled) return;
    onFilesSelected(Array.from(e.dataTransfer.files));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled || !e.target.files) return;
    onFilesSelected(Array.from(e.target.files));
    e.target.value = '';
  };

  return (
    <Paper
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        p: 4,
        border: `2px dashed ${isDragging ? '#1976d2' : '#ccc'}`,
        borderRadius: 2,
        textAlign: 'center',
        bgcolor: disabled ? '#f5f5f5' : isDragging ? '#e3f2fd' : '#fafafa',
        transition: 'all 0.3s ease',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.7 : 1,
        '&:hover': disabled
          ? {}
          : {
              borderColor: '#1976d2',
              bgcolor: '#f5f5f5',
            },
      }}
      onClick={() => {
        if (!disabled) fileInputRef.current?.click();
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={disabled}
      />

      <CloudUpload sx={{ fontSize: 64, color: '#1976d2', mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Drag and drop files here, or click to select
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Supported formats: {supportedFormatsText}
      </Typography>
      <Box sx={{ mt: 2 }}>
        <Chip
          icon={<Psychology />}
          label="Vision AI Enabled"
          size="small"
          sx={{ bgcolor: '#009688', color: 'white' }}
        />
      </Box>
    </Paper>
  );
}
